import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSites } from "@/contexts/SiteContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, ExternalLink, Loader2, MapPin, Plus, RefreshCw, Save, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ServiceItemRow } from "@/components/gbp/ServiceItemRow";
import { GbpSuggestionsPanel } from "@/components/gbp/GbpSuggestionsPanel";
import { GbpProfilePanel } from "@/components/gbp/GbpProfilePanel";

const RETURN_TO = "/google-business";
const locId = (loc) => (loc || "").split("/").pop();

export default function GoogleBusiness() {
  const { activeSite } = useSites();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [authExpired, setAuthExpired] = useState(false);
  const [locations, setLocations] = useState(null);
  const [locError, setLocError] = useState(null);
  const [loc, setLoc] = useState("");
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [addType, setAddType] = useState("");
  const pollRef = useRef(null);

  useEffect(() => {
    const flag = searchParams.get("gbp");
    if (flag === "connected") toast.success("Google Business Profile connecté.");
    else if (flag === "connected_quota_pending") toast.warning("Connecté, mais l'accès API Google Business n'est pas encore approuvé (quota à 0).");
    if (flag) { searchParams.delete("gbp"); setSearchParams(searchParams, { replace: true }); }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    api.get("/gbp/status").then(({ data: s }) => { setStatus(s); setAuthExpired(!!s.auth_expired); }).catch(() => setStatus({ server_configured: false }));
    return () => clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    if (!status?.connected || status.scope_ok === false || status.auth_expired) return;
    api.get("/gbp/locations")
      .then(({ data: d }) => { setLocations(d.locations || []); if (d.locations?.length) setLoc(d.locations[0].location); })
      .catch((err) => {
        if (err?.response?.status === 401) { setAuthExpired(true); return; }
        setLocations([]); setLocError(err?.response?.data?.detail || "Impossible de lister vos établissements.");
      });
  }, [status]);

  const load = useCallback(async () => {
    if (!loc) return;
    setLoading(true);
    setSuggestions(null);
    try {
      const { data: d } = await api.get(`/gbp/locations/${locId(loc)}/services`);
      setData(d);
      setItems(d.items || []);
      setDirty(false);
    } catch (err) {
      setData(null);
      if (err?.response?.status === 401) { setAuthExpired(true); return; }
      toast.error(err?.response?.data?.detail || "Lecture des services impossible.");
    } finally {
      setLoading(false);
    }
  }, [loc]);

  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    try {
      const { data: d } = await api.get("/gbp/login", { params: { return_to: RETURN_TO } });
      window.location.href = d.authorization_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Connexion impossible");
    }
  };
  const reconnect = async () => { try { await api.post("/gbp/disconnect"); } catch { /* ignore */ } connect(); };

  const update = (i, next) => { setItems((prev) => prev.map((it, k) => (k === i ? next : it))); setDirty(true); };
  const remove = (i) => { setItems((prev) => prev.filter((_, k) => k !== i)); setDirty(true); };
  const addFree = () => {
    setItems((prev) => [...prev, { kind: "free", display_name: "", description: "", price_amount: null, price_currency: "CHF", language_code: data?.language_code }]);
    setDirty(true);
  };
  const addStructured = (stid) => {
    const t = (data?.available_service_types || []).find((x) => x.service_type_id === stid);
    if (!t) return;
    setItems((prev) => [...prev, { kind: "structured", service_type_id: t.service_type_id, display_name: t.display_name, description: "", price_amount: null, price_currency: "CHF" }]);
    setDirty(true);
    setAddType("");
  };
  const addSuggested = (s) => {
    if (s.service_type_id) return addStructured(s.service_type_id);
    setItems((prev) => [...prev, { kind: "free", display_name: s.display_name, description: s.description, price_amount: null, price_currency: "CHF", language_code: data?.language_code }]);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data: d } = await api.put(`/gbp/locations/${locId(loc)}/services`, { items });
      setItems(d.items || items);
      setDirty(false);
      toast.success(`${d.count} service(s) enregistré(s) sur votre fiche Google.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Enregistrement refusé par Google.");
    } finally {
      setSaving(false);
    }
  };

  const suggest = async () => {
    if (!activeSite) return toast.error("Sélectionnez un site actif.");
    setSuggesting(true);
    try {
      const { data: d } = await api.post(`/sites/${activeSite.id}/gbp-suggestions`, { location_id: locId(loc) || null });
      toast.info("L'IA prépare vos services et fiches produits (20-40 s)…");
      let attempts = 0;
      pollRef.current = setInterval(async () => {
        attempts += 1;
        if (attempts > 40) { clearInterval(pollRef.current); setSuggesting(false); toast.error("Suggestions trop longues. Réessayez."); return; }
        try {
          const { data: job } = await api.get(`/content/jobs/${d.job_id}`);
          if (job.status === "completed") {
            clearInterval(pollRef.current); setSuggesting(false); setSuggestions(job.result || { services: [], products: [] });
            toast.success(`${job.result?.services?.length || 0} services et ${job.result?.products?.length || 0} fiches produits proposés.`);
          } else if (job.status === "failed") {
            clearInterval(pollRef.current); setSuggesting(false); toast.error(job.error || "Suggestions échouées.");
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (err) {
      setSuggesting(false);
      toast.error(err?.response?.data?.detail || "Impossible de lancer les suggestions.");
    }
  };

  const usedNames = new Set(items.map((i) => (i.display_name || "").trim().toLowerCase()));
  const usedTypes = new Set(items.filter((i) => i.kind === "structured").map((i) => i.service_type_id));
  const freeTypes = (data?.available_service_types || []).filter((t) => !usedTypes.has(t.service_type_id));
  const invalid = items.some((i) => i.kind === "free" && !(i.display_name || "").trim());

  return (
    <div className="p-8 max-w-6xl" data-testid="google-business-page">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2.5 mb-1">
            <div className="w-9 h-9 rounded-md bg-[#002FA7] flex items-center justify-center">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <h1 className="font-display text-2xl font-bold text-slate-950">Fiche Google</h1>
          </div>
          <p className="text-sm text-slate-600 max-w-2xl">
            Gérez la <b>description</b>, les <b>horaires</b>, les <b>coordonnées</b> et les <b>services</b> de votre fiche
            Google Business Profile directement ici. L'IA propose textes, services et fiches <b>Produits</b> à partir de
            votre profil business. Rien n'est envoyé à Google sans votre clic.
          </p>
        </div>
        {status?.connected && (
          <div className="text-xs text-slate-500 text-right" data-testid="gbp-connected-info">
            Connecté{status.email ? ` · ${status.email}` : ""}
            <button onClick={reconnect} className="block ml-auto text-[#002FA7] hover:underline mt-0.5" data-testid="gbp-reconnect-link">Changer de compte</button>
          </div>
        )}
      </div>

      {!status ? (
        <div className="text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Chargement…</div>
      ) : !status.server_configured ? (
        <div className="border border-amber-300 bg-amber-50 rounded-lg p-5 text-sm text-amber-900" data-testid="gbp-not-configured">
          Google Business Profile n'est pas configuré côté serveur (GBP_CLIENT_ID / GBP_CLIENT_SECRET / GBP_REDIRECT_URI).
        </div>
      ) : !status.connected ? (
        <div className="border border-slate-200 bg-white rounded-lg p-6 flex items-center justify-between gap-4 flex-wrap" data-testid="gbp-connect-panel">
          <div className="text-sm text-slate-700">Connectez le compte Google propriétaire de votre fiche pour lire et modifier ses services.</div>
          <Button onClick={connect} className="bg-[#002FA7] hover:bg-[#00248A] text-white" data-testid="gbp-connect-button">
            <MapPin className="w-4 h-4 mr-2" /> Connecter Google Business
          </Button>
        </div>
      ) : authExpired ? (
        <div className="border border-red-300 bg-red-50 rounded-lg p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="gbp-auth-expired-panel">
          <div className="text-sm text-red-800 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              <b>Connexion Google Business expirée ou révoquée</b>{status.email ? ` (${status.email})` : ""}. Google a invalidé le jeton
              d'accès : reconnectez le compte propriétaire de la fiche pour reprendre la lecture et les modifications.
            </span>
          </div>
          <Button onClick={reconnect} className="bg-red-600 hover:bg-red-700 text-white" data-testid="gbp-auth-expired-reconnect">
            <RefreshCw className="w-4 h-4 mr-2" /> Reconnecter Google Business
          </Button>
        </div>
      ) : status.scope_ok === false ? (
        <div className="border border-amber-300 bg-amber-50 rounded-lg p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="gbp-scope-panel">
          <div className="text-sm text-amber-900 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Permission « business.manage » manquante sur la connexion actuelle.</div>
          <Button onClick={reconnect} variant="outline" className="border-amber-400 text-amber-900" data-testid="gbp-reconnect-button">Reconnecter</Button>
        </div>
      ) : (
        <>
          <div className="border border-slate-200 bg-white rounded-lg p-5 mb-6 flex items-center gap-3 flex-wrap" data-testid="gbp-location-panel">
            <span className="text-sm font-semibold text-slate-900">Établissement</span>
            {locations === null ? (
              <span className="text-xs text-slate-500"><Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" />Chargement…</span>
            ) : locError ? (
              <span className="text-xs text-red-600" data-testid="gbp-location-error">{locError}</span>
            ) : (
              <select value={loc} onChange={(e) => setLoc(e.target.value)} data-testid="gbp-location-select"
                className="border border-slate-300 rounded-md px-2.5 py-1.5 text-sm bg-white min-w-[260px]">
                {locations.map((l) => <option key={l.location} value={l.location}>{l.title}</option>)}
              </select>
            )}
            {data?.primary_category && (
              <span className="text-xs text-slate-500" data-testid="gbp-primary-category">Catégorie : {data.primary_category.name || data.primary_category.id}</span>
            )}
            {data?.maps_uri && (
              <a href={data.maps_uri} target="_blank" rel="noreferrer" className="text-xs text-[#002FA7] hover:underline inline-flex items-center gap-1">
                Voir sur Maps <ExternalLink className="w-3 h-3" />
              </a>
            )}
            <button onClick={load} disabled={loading || !loc} className="ml-auto text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1" data-testid="gbp-reload-button">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Recharger depuis Google
            </button>
          </div>

          {data && !data.can_modify_service_list && (
            <div className="border border-amber-300 bg-amber-50 rounded-lg px-4 py-3 mb-4 text-xs text-amber-900 flex items-center gap-2" data-testid="gbp-cannot-modify">
              <AlertTriangle className="w-4 h-4" /> Google n'autorise pas la modification des services de cet établissement via l'API. La liste est en lecture seule.
            </div>
          )}

          {data?.profile && (
            <GbpProfilePanel
              profile={data.profile}
              locationId={locId(loc)}
              siteId={activeSite?.id}
              onSaved={(p) => setData((d) => ({ ...d, profile: p }))}
            />
          )}

          {data && (
            <div className="border border-slate-200 bg-white rounded-lg p-5" data-testid="gbp-services-panel">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                <div className="text-sm font-semibold text-slate-900">
                  Services de la fiche <span className="text-slate-400 font-normal">({items.length})</span>
                  {dirty && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800" data-testid="gbp-dirty-badge">Non enregistré</span>}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button variant="outline" size="sm" onClick={suggest} disabled={suggesting} data-testid="gbp-suggest-button" className="border-[#002FA7]/40 text-[#002FA7]">
                    {suggesting ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> IA en cours…</> : <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Proposer services & produits par IA</>}
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" disabled={!dirty || saving || invalid || !data.can_modify_service_list} data-testid="gbp-save-button" className="bg-[#002FA7] hover:bg-[#00248A] text-white">
                        {saving ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Envoi…</> : <><Save className="w-3.5 h-3.5 mr-1.5" /> Enregistrer sur Google</>}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Remplacer les services sur Google ?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Google remplace la liste complète : {items.length} service(s) seront publiés sur « {data.title} ».
                          Les services retirés ici disparaîtront de la fiche.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="gbp-save-cancel">Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={save} data-testid="gbp-save-confirm" className="bg-[#002FA7] hover:bg-[#00248A]">Publier sur Google</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>

              {items.length === 0 ? (
                <p className="text-xs text-slate-500 mb-3" data-testid="gbp-services-empty">Aucun service sur cette fiche pour l'instant.</p>
              ) : (
                <div className="space-y-2 mb-3">
                  {items.map((it, i) => <ServiceItemRow key={i} item={it} index={i} onChange={update} onRemove={remove} />)}
                </div>
              )}

              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={addFree} data-testid="gbp-add-free-button"
                  className="inline-flex items-center gap-1 text-xs font-medium text-[#002FA7] border border-[#002FA7]/30 rounded-md px-3 py-1.5 hover:bg-blue-50">
                  <Plus className="w-3.5 h-3.5" /> Service personnalisé
                </button>
                {freeTypes.length > 0 && (
                  <select value={addType} onChange={(e) => addStructured(e.target.value)} data-testid="gbp-add-structured-select"
                    className="text-xs border border-slate-300 rounded-md px-2 py-1.5 bg-white">
                    <option value="">+ Service prédéfini Google…</option>
                    {freeTypes.map((t) => <option key={t.service_type_id} value={t.service_type_id}>{t.display_name}</option>)}
                  </select>
                )}
                {invalid && <span className="text-xs text-red-600" data-testid="gbp-invalid-hint">Un service personnalisé n'a pas de nom.</span>}
              </div>
            </div>
          )}

          {suggestions && (
            <div className="mt-6">
              <GbpSuggestionsPanel suggestions={suggestions} usedNames={usedNames} onAddService={addSuggested} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
